import { useMemo, useRef, useState } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useAccount } from "wagmi";
import { parseUnits } from "viem";
import {
  CADENAS, cotizarMejor, esNativo, type Cotizacion, type TokenPuente,
} from "../lib/puente";
import {
  CLAVE_TOKENS, enviarCotizacion, leerSaldos, precioDeGas, refrescarSaldos, traerTokens, traducir,
} from "./usePuente";
import type { EstadoOp } from "../components/ModalOperacion";

/**
 * BARRIDO DE POLVO. Convertir de una sentada todos los restos —de TODAS las cadenas— al estable
 * que el usuario elija.
 *
 * El polvo no es un caso raro: cada puente, cada swap y cada piscina cerrada deja migajas de
 * cuatro o cinco tokens sueltos que nadie va a mover una por una porque cada una son dos firmas y
 * tres pantallas. Aqui se ven todas juntas, se marcan las que se quieran y se van en fila.
 *
 * Cobra lo mismo que el puente a mano (0,10%): la comision no se toca aqui, va dentro de
 * `cotizarMejor` — asi no hay dos sitios donde se decida cuanto cobramos.
 */

export type Moneda = TokenPuente & { cadena: number };
export type Destino = { cadena: number; token: TokenPuente };

export const clave = (cadena: number, dir: string) => `${cadena}:${dir.toLowerCase()}`;
export const valorUsd = (t: TokenPuente) => (t.saldo ?? 0) * (parseFloat(t.priceUSD ?? "0") || 0);

/**
 * Lo que hay en las seis cadenas a la vez. Una consulta por cadena y no una gigante: si HyperEVM
 * tarda o su RPC se cae, las otras cinco ya se ven — con una sola consulta la pantalla se queda
 * entera en blanco por culpa de la mas lenta.
 */
export function useCarteras() {
  const { address } = useAccount();
  const qc = useQueryClient();

  const consultas = useQueries({
    queries: CADENAS.map((c) => ({
      queryKey: ["cartera-puente", c.id, address],
      enabled: !!address,
      staleTime: 30_000,
      retry: false,
      queryFn: async (): Promise<Moneda[]> => {
        // Por `fetchQuery` y no por `fetch` a pelo: comparte cache con el selector de tokens del
        // puente, que ya se ha traido esas mismas listas de cientos de tokens.
        const tokens = await qc.fetchQuery({
          queryKey: CLAVE_TOKENS(c.id),
          staleTime: 60 * 60 * 1000,
          queryFn: () => traerTokens(c.id),
        });
        const con = await leerSaldos(c.id, address!, tokens);
        return con.map((t) => ({ ...t, cadena: c.id }));
      },
    })),
  });

  // La firma evita rehacer las listas en cada render: `useQueries` devuelve un array nuevo siempre.
  const firma = consultas.map((r) => `${r.status}:${r.dataUpdatedAt}`).join("|");

  return useMemo(() => {
    const redes = CADENAS.map((c, i) => ({
      ...c,
      cargando: consultas[i]!.isPending && !!address,
      fallo: consultas[i]!.isError,
      monedas: consultas[i]!.data ?? [],
    }));
    const monedas = redes.flatMap((r) => r.monedas);
    return {
      redes,
      monedas,
      total: monedas.reduce((s, m) => s + valorUsd(m), 0),
      cargando: redes.some((r) => r.cargando),
      refrescar: () => qc.invalidateQueries({ queryKey: ["cartera-puente"] }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma, address]);
}

export type FaseFila = "espera" | "cotizando" | "lista" | "sinruta" | "enviando" | "hecha" | "fallo";
export type Fila = {
  fase: FaseFila; q?: Cotizacion; error?: string; hash?: string; nota?: string;
  /** simbolo y cadena copiados al terminar: al refrescar saldos la moneda barrida desaparece de
   *  la lista (ya no tiene saldo) y con ella se iria el enlace a su transaccion. */
  sim?: string; cadena?: number;
};

/**
 * Cuanto gas hay que dejar sin barrer en cada cadena. Del nativo NO se puede mandar el 100%: la
 * propia transaccion se paga con el, y una cotizacion por el saldo entero revierte siempre. Aqui
 * se aparta lo que cuesta un envio (400k de gas al precio de ahora) y se barre el resto; si no
 * queda resto, la moneda se marca como «no llega ni para el gas» en vez de fallar al firmar.
 */
async function reservasDeGas(monedas: Moneda[]): Promise<Map<number, bigint>> {
  const cadenas = [...new Set(monedas.filter((m) => esNativo(m.address)).map((m) => m.cadena))];
  const pares = await Promise.all(cadenas.map(async (id) => {
    const p = await precioDeGas(id).catch(() => undefined);
    return [id, (p ?? 1_000_000_000n) * 400_000n] as const;
  }));
  return new Map(pares);
}

function importeDe(m: Moneda, reservas: Map<number, bigint>): bigint {
  const crudo = BigInt(m.crudo ?? "0");
  if (!esNativo(m.address)) return crudo;
  return crudo - (reservas.get(m.cadena) ?? 0n);
}

/** Por que no hay ruta, en un renglon. Casi siempre es el minimo del puente, y decirlo ahorra
 *  al usuario probar a ciegas con cada moneda. */
function porQueNoHayRuta(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e);
  if (m === "SIN_GAS") return "no llega ni para el gas de esa red";
  if (/minimum|too low|small|amount|dust/i.test(m)) return "por debajo del mínimo del puente";
  if (/unsupported|no route|not supported/i.test(m)) return "ningún puente la mueve";
  if (/liquidity/i.test(m)) return "sin liquidez ahora mismo";
  return "sin ruta ahora mismo";
}

/** Lanza `fn` sobre todos los elementos con como mucho `n` a la vez. Las APIs de ruta contestan
 *  en segundos y de una en una un barrido de veinte monedas se hace eterno; de golpe, nos cortan. */
async function enTanda<T>(items: T[], n: number, fn: (t: T) => Promise<void>) {
  const cola = [...items];
  await Promise.all(Array.from({ length: Math.min(n, cola.length) }, async () => {
    while (cola.length) await fn(cola.shift()!);
  }));
}

export function useBarrido() {
  const { address } = useAccount();
  const qc = useQueryClient();
  const [filas, setFilas] = useState<Record<string, Fila>>({});
  const [ocupado, setOcupado] = useState<null | "cotizando" | "barriendo">(null);
  const [progreso, setProgreso] = useState({ hechas: 0, total: 0 });
  const [resumen, setResumen] = useState<{ hechas: number; fallos: number; parado: boolean } | null>(null);
  const parada = useRef(false);

  const poner = (k: string, f: Fila) => setFilas((x) => ({ ...x, [k]: f }));

  /** Pide ruta para cada moneda marcada, sin firmar nada. Es el «cuanto me va a quedar» previo. */
  async function cotizar(monedas: Moneda[], destino: Destino) {
    if (!address || !monedas.length) return;
    parada.current = false;
    setResumen(null);
    setOcupado("cotizando");
    setProgreso({ hechas: 0, total: monedas.length });
    setFilas(Object.fromEntries(monedas.map((m) => [clave(m.cadena, m.address), { fase: "cotizando" } as Fila])));

    const reservas = await reservasDeGas(monedas);
    await enTanda(monedas, 3, async (m) => {
      const k = clave(m.cadena, m.address);
      if (parada.current) { poner(k, { fase: "espera" }); return; }
      try {
        const importe = importeDe(m, reservas);
        if (importe <= 0n) throw new Error("SIN_GAS");
        const q = await cotizarMejor(address!, m.cadena, destino.cadena, m, destino.token, importe.toString());
        poner(k, { fase: "lista", q });
      } catch (e) {
        poner(k, { fase: "sinruta", error: porQueNoHayRuta(e) });
      } finally {
        setProgreso((p) => ({ ...p, hechas: p.hechas + 1 }));
      }
    });
    setOcupado(null);
  }

  /**
   * Barre de verdad. Dos decisiones que importan:
   *
   *  · Se ordena POR CADENA. Cada salto de red es una ventana de la wallet: barrer cuatro monedas
   *    de Base seguidas pide una, e ir saltando pide cuatro.
   *  · La ruta se vuelve a pedir JUSTO antes de firmar cada una. La cotizacion del boton
   *    «calcular» es de hace un minuto y las rutas caducan; firmar una caducada es pagar gas para
   *    que revierta.
   */
  async function barrer(monedas: Moneda[], destino: Destino) {
    if (!address || !monedas.length) return;
    parada.current = false;
    setResumen(null);
    setOcupado("barriendo");
    setProgreso({ hechas: 0, total: monedas.length });

    const orden = [...monedas].sort((a, b) => a.cadena - b.cadena || valorUsd(b) - valorUsd(a));
    const reservas = await reservasDeGas(orden);
    let hechas = 0, fallos = 0;

    for (const m of orden) {
      if (parada.current) break;
      const k = clave(m.cadena, m.address);
      try {
        poner(k, { fase: "enviando", nota: "buscando ruta…" });
        const importe = importeDe(m, reservas);
        if (importe <= 0n) throw new Error("SIN_GAS");
        const q = await cotizarMejor(address!, m.cadena, destino.cadena, m, destino.token, importe.toString());
        let ultimo: string | undefined;
        const hash = await enviarCotizacion(q, m.cadena, m.address, address!, (fase, h) => {
          if (h) ultimo = h;
          poner(k, { fase: "enviando", nota: fase, hash: ultimo });
        });
        poner(k, { fase: "hecha", hash, q, sim: m.symbol, cadena: m.cadena });
        hechas++;
      } catch (e) {
        const msg = traducir(e);
        // Cancelar una firma es «para», no «salta esta»: si no, la wallet sigue abriendo una
        // ventana por moneda y el usuario tiene que rechazarlas todas a mano.
        if (/Cancelaste la firma/.test(msg)) {
          poner(k, { fase: "lista" });
          parada.current = true;
        } else {
          poner(k, { fase: "fallo", error: msg });
          fallos++;
        }
      }
      setProgreso((p) => ({ ...p, hechas: p.hechas + 1 }));
    }

    await refrescarSaldos(qc);
    setOcupado(null);
    setResumen({ hechas, fallos, parado: parada.current });
  }

  return {
    filas, ocupado, progreso, resumen,
    cotizar, barrer,
    /** Corta despues de la moneda que se este firmando: interrumpir a media firma no se puede. */
    parar: () => { parada.current = true; },
    limpiar: () => { setFilas({}); setResumen(null); setProgreso({ hechas: 0, total: 0 }); },
  };
}

/** Cuánto gas (en $) traer de una vez. Basta para varias transacciones en la red destino. */
export const REFUEL_USD = 4;

/**
 * REFUEL: traer gas nativo a una cadena donde el usuario tiene tokens pero NADA de nativo para
 * pagar el gas. No se puede arrancar gas desde cero en la propia cadena (convertir un token a gas
 * es TAMBIÉN una transacción que necesita gas), así que se trae un poco del nativo desde otra
 * cadena donde SÍ hay: un micro-bridge native→native con Relay/LI.FI, el mismo `cotizarMejor` +
 * `enviarCotizacion` del barrido. El feedback va en el `ModalOperacion` (logo → cargando → ok/error).
 */
export function useRefuel() {
  const { address } = useAccount();
  const qc = useQueryClient();
  const [estado, setEstado] = useState<EstadoOp | null>(null);

  async function traerGas(fuente: Moneda, hastaCadena: number) {
    if (!address) return;
    const dest = CADENAS.find((c) => c.id === hastaCadena);
    const fuenteCad = CADENAS.find((c) => c.id === fuente.cadena);
    const nombre = dest?.nombre ?? "la red";
    const enc = `Trayendo gas a ${nombre}`;
    setEstado({ fase: "cargando", titulo: enc, sub: "Buscando la mejor ruta…" });
    try {
      // El nativo de destino se saca de SU lista de tokens (la dirección exacta que esperan los
      // puentes): con saldo 0 no aparece en la cartera, así que no se puede tomar de ahí.
      const tokensDest = await qc.fetchQuery({
        queryKey: CLAVE_TOKENS(hastaCadena), staleTime: 60 * 60 * 1000, queryFn: () => traerTokens(hastaCadena),
      });
      const destNativo = tokensDest.find((t) => esNativo(t.address));
      if (!destNativo) throw new Error(`No encuentro el nativo de ${nombre}.`);
      const px = parseFloat(fuente.priceUSD ?? "0");
      if (!(px > 0)) throw new Error("No tengo el precio del gas de origen para calcular cuánto traer.");
      const dec = fuente.decimals ?? 18;
      const importe = parseUnits((REFUEL_USD / px).toFixed(dec), dec);

      setEstado({ fase: "cargando", titulo: enc, sub: `Enviando ~$${REFUEL_USD} de ${fuente.symbol} desde ${fuenteCad?.nombre}…` });
      const q = await cotizarMejor(address, fuente.cadena, hastaCadena, fuente, destNativo, importe.toString());
      const hash = await enviarCotizacion(q, fuente.cadena, fuente.address, address, (fase, h) => {
        setEstado({ fase: "cargando", titulo: enc, sub: fase, ...(h ? { txUrl: `${fuenteCad?.explorador}/tx/${h}` } : {}) });
      });
      setEstado({
        fase: "ok", titulo: "Gas en camino",
        sub: `Llega a ${nombre} en ~1-2 min. Cuando aterrice, refresca los saldos y ya podrás mover tus tokens.`,
        ...(hash ? { txUrl: `${fuenteCad?.explorador}/tx/${hash}` } : {}),
      });
      refrescarSaldos(qc);
    } catch (e) {
      setEstado({ fase: "error", titulo: "", mensaje: traducir(e) });
    }
  }

  return { estado, traerGas, cerrar: () => setEstado(null) };
}
